/**
 * Store Service Layer - 业务逻辑层 + Mock 适配
 * 页面组件只调用此 Service 层,不直接调用 API
 *
 * 注意：后端接口中门店格式字段名为 clusterSection，
 * 前端统一使用 storeFormat，在此 Service 层做双向映射。
 */

import { config } from '../../../config';
import * as storeMock from '../../../mocks/master-data/store';
import type { ApiResponse } from '../../../types/common';
import type {
  Store,
  PageStoreRequest,
  PageStoreData,
} from '../../../types/master-data/store';
import httpClient from '../../httpClient';
import { API_ENDPOINTS } from '../../../utils/apiEndpoints';

/**
 * 将后端返回的单条门店数据中的 clusterSection 映射为前端的 storeFormat
 */
function mapStoreFromBackend(raw: Record<string, any>): Store {
  const { clusterSection, ...rest } = raw;
  return {
    ...rest,
    storeFormat: clusterSection ?? rest.storeFormat,
  } as Store;
}

/**
 * 将前端请求参数中的 storeFormat 映射为后端的 clusterSection
 */
function mapPageRequestToBackend(params: PageStoreRequest): Record<string, any> {
  const { storeFormat, ...rest } = params;
  return {
    ...rest,
    ...(storeFormat !== undefined ? { clusterSection: storeFormat } : {}),
  };
}

/**
 * 分页查询门店 (符合 domain-frontend-spec 规范)
 */
export async function pageStores(
  params: PageStoreRequest
): Promise<ApiResponse<PageStoreData>> {
  if (config.mock) {
    return storeMock.mockPageStores(params);
  }

  const backendParams = mapPageRequestToBackend(params);
  // axios 拦截器已解包 AxiosResponse，返回的直接是 ApiResponse
  const response = await httpClient.post(API_ENDPOINTS.STORE.PAGE, backendParams) as unknown as ApiResponse<PageStoreData>;

  if (response.data?.tableData) {
    response.data.tableData = response.data.tableData.map(mapStoreFromBackend);
  }

  return response;
}

/**
 * 获取门店列表 (符合 domain-frontend-spec 规范)
 */
export async function listStores(
  params?: Partial<Store>
): Promise<ApiResponse<Store[]>> {
  if (config.mock) {
    return storeMock.mockListStores(params);
  }

  // axios 拦截器已解包 AxiosResponse，返回的直接是 ApiResponse
  const response = await httpClient.post(API_ENDPOINTS.STORE.LIST, params || {}) as unknown as ApiResponse<Store[]>;

  if (Array.isArray(response.data)) {
    response.data = response.data.map(mapStoreFromBackend);
  }

  return response;
}

/**
 * 导入门店文件（模拟 SFTP 文件传输）
 * 上传文件 → OSS → 发送 MQ → Processor 消费入库
 */
export async function importStoreFile(
  file: File
): Promise<ApiResponse<{ downloadUrl: string; fileName: string } | null>> {
  const formData = new FormData();
  formData.append('file', file);
  return httpClient.post(API_ENDPOINTS.STORE.IMPORT, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

/**
 * 导出门店（携带当前筛选条件）
 * 后端返回 Excel 下载 URL
 */
export async function exportStores(
  params: Partial<PageStoreRequest>
): Promise<ApiResponse<string>> {
  const backendParams = mapPageRequestToBackend(params as PageStoreRequest);
  return httpClient.post(API_ENDPOINTS.STORE.EXPORT, backendParams);
}
